import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { useUpdateMeMutation } from "@/pages/Private/redux/account/account.api";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { ChangeEmail, ChangeEmailSchema } from "../schema/account";

interface ChangeEmailModalProps {
	isVisible: boolean;
	currentEmail: string;
	handleClose: () => void;
}

export const ChangeEmailModal: FunctionComponent<ChangeEmailModalProps> = ({
	isVisible,
	currentEmail,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);

	const [updateMe, { isLoading, error }] = useUpdateMeMutation();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<ChangeEmail>({
		defaultValues: { email: currentEmail },
		resolver: zodResolver(ChangeEmailSchema),
	});

	const formErrors = Object.values(errors).map((err) => err?.message) as ERROR_TYPE[];

	const handleCloseModal = () => {
		reset({ email: currentEmail });
		handleClose();
	};

	const onSubmit = async (values: ChangeEmail) => {
		try {
			await updateMe({ email: values.email }).unwrap();
			handleCloseModal();
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={handleCloseModal}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isVisible}
			submitButtonText={t("basics.saveChanges")}
			title={ts("changeEmail")}
		>
			<div className="mt-2">
				<Controller
					control={control}
					name="email"
					render={({ field }) => (
						<InputField
							error={errors.email?.message}
							label={t("basics.email")}
							name="email"
							placeholder={t("basics.email")}
							type="email"
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
		</Modal>
	);
};
